import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { applyNoConditions } from 'api/operations/electionService';
import { SubscriberImqModule } from 'store/modules/subscriber-imq/subscriberImqModule';
import { HealthQuestionnaireDomainParams } from './types';

export default abstract class IHealthQuestionnaireEntryPage {
  protected readonly _domainParams: HealthQuestionnaireDomainParams;
  public readonly subscriberImqModule: SubscriberImqModule;

  private _subscriberHasMedicalConditions: boolean | null = null;
  private _isInitialDataLoading = true;
  private _isActionLoading = false;

  public get domainParams(): HealthQuestionnaireDomainParams {
    return this._domainParams;
  }

  public get subscriberHasMedicalConditions(): boolean | null {
    return this._subscriberHasMedicalConditions;
  }

  public get isActionLoading(): boolean {
    return this._isActionLoading;
  }

  public get canContinue(): boolean {
    return this.subscriberHasMedicalConditions !== null && !this.isInitialLoading && !this.isActionLoading;
  }

  public abstract get isInitialLoading(): boolean;

  protected abstract redirectWhenQuestionnaireAlreadyStarted(): void;

  protected abstract redirectAfterAnswer(hasConditions: boolean): void;

  public setIsInitialDataLoading(status: boolean): void {
    this._isInitialDataLoading = status;
  }

  protected setIsActionLoading(status: boolean): void {
    this._isActionLoading = status;
  }

  public setMedicalHistoryAnswer(answer: boolean): void {
    this._subscriberHasMedicalConditions = answer;
  }

  protected get isPageContentLoading(): boolean {
    return this._isInitialDataLoading;
  }

  public async getInitialData(): Promise<void> {
    this.setIsInitialDataLoading(true);
    await this.subscriberImqModule.getSubscriber();

    runInAction(() => {
      if (this.subscriberImqModule.questionnaireWasStarted) {
        this.redirectWhenQuestionnaireAlreadyStarted();
      } else {
        this.setIsInitialDataLoading(false);
      }
    });
  }

  public async completeStep(): Promise<void> {
    if (this.subscriberHasMedicalConditions === null) {
      return;
    }

    if (this.subscriberHasMedicalConditions === true) {
      this.redirectAfterAnswer(true);
      return;
    }

    this.setIsActionLoading(true);
    await applyNoConditions(this.domainParams.subscriberId);
    runInAction(() => {
      this.setIsActionLoading(false);
      this.redirectAfterAnswer(false);
    });
  }

  constructor(domainParams: HealthQuestionnaireDomainParams) {
    this._domainParams = domainParams;
    this.subscriberImqModule = new SubscriberImqModule({ subscriberId: domainParams.subscriberId });

    makeObservable<
      IHealthQuestionnaireEntryPage,
      | '_subscriberHasMedicalConditions'
      | '_isInitialDataLoading'
      | '_isActionLoading'
      | 'setIsActionLoading'
      | 'isPageContentLoading'
    >(this, {
      _subscriberHasMedicalConditions: observable,
      _isInitialDataLoading: observable,
      _isActionLoading: observable,
      domainParams: computed,
      subscriberHasMedicalConditions: computed,
      isActionLoading: computed,
      canContinue: computed,
      isPageContentLoading: computed,
      setIsInitialDataLoading: action.bound,
      setIsActionLoading: action.bound,
      setMedicalHistoryAnswer: action.bound,
      getInitialData: action.bound,
      completeStep: action.bound,
    });
  }
}
